import React, { useEffect, useState } from "react";
import { OverlayTrigger, Placeholder, Toast, ToastContainer, Tooltip } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstTabs from "../../components/DstTabs/DstTabs";
import { useSession } from "../../contexts/SessionContext";
import { TabData } from "../../models/TabModel";
import { validateUserPermission } from "../../services/PermissionGuard";
import ShortcutService from "../../services/ShortcutService";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { fetchMeeting, getBadgeData } from "./Meeting.function";
import AbsenceProofs from "./components/AbsenceProofs/AbsenceProofs";
import Attendance from "./components/Attendance/Attendance";
import ManageMeeting from "./components/ManageMeeting/ManageMeeting";
import MeetingLogs from "./components/MeetingLogs/MeetingLogs";
import Resource from "./components/Resources/Resources";

const _formatMeetingTime = (start: string, end: string, language: string): string => {
  const startDate = new Date(start);
  const formattedDate = formatDateHumanly(language, startDate.toISOString(), false);

  const startTime = startDate.toLocaleTimeString(language, { hour: "2-digit", minute: "2-digit" });
  const endTime = new Date(end).toLocaleTimeString(language, { hour: "2-digit", minute: "2-digit" });

  return `${formattedDate} ${startTime} - ${endTime}`;
};

const Meeting = () => {
  const navigate = useNavigate();
  const { language, permissions } = useSession();
  const { meeting_id } = useParams<{ meeting_id: string }>();
  const [meeting, setMeeting] = useState<any | null>(null);
  const [activeKey, setActiveKey] = useState<string>("manage_meeting");
  const [loaded, setLoaded] = useState(false);
  const [showCopyShortcutToast, setShowCopyShortcutToast] = useState(false);

  const tabsData = [
    {
      id: "manage_meeting",
      title: (
        <React.Fragment>
          <span className="dst-icon-settings-04 me-2" />
          {translate(language, "PAGES.MEETING.MEETING_TABS.MANAGE_MEETING")}
        </React.Fragment>
      ),
      content: (
        <ManageMeeting
          meeting={meeting}
          refreshMeeting={() => meeting_id && fetchMeeting(meeting_id, setMeeting, setLoaded)}
        />
      ),
    },
    validateUserPermission([{ methods: ["GET"], route: "/meetings/*/attendance" }], permissions)
      ? {
          id: "attendance",
          title: (
            <React.Fragment>
              <span className="dst-icon-clipboard-check me-2" />
              {translate(language, "PAGES.MEETING.MEETING_TABS.ATTENDANCE")}
            </React.Fragment>
          ),
          content: (
            <Attendance
              meetingId={meeting_id!}
              edusignLocked={meeting?.edusign_locked ?? false}
              edusignCourseId={meeting?.edusign_course_id ?? false}
              isMeetingFinished={meeting?.dt_end ? new Date(meeting.dt_end).getTime() < Date.now() - 1000 : false}
              isMeetingMandatory={meeting?.type?.is_mandatory}
              refreshMeeting={() => meeting_id && fetchMeeting(meeting_id, setMeeting, setLoaded)}
            />
          ),
        }
      : undefined,
    validateUserPermission([{ methods: ["GET"], route: "/meetings/*/resources" }], permissions)
      ? {
          id: "resources",
          title: (
            <React.Fragment>
              <span className="dst-icon-link-03 me-2" />
              {translate(language, "PAGES.MEETING.MEETING_TABS.RESOURCES")}
            </React.Fragment>
          ),
          content: <Resource meetingId={meeting_id!} language={language} />,
        }
      : undefined,
    validateUserPermission([{ methods: ["GET"], route: "/meetings/*/absence-proofs" }], permissions)
      ? {
          id: "absence_proofs",
          title: (
            <React.Fragment>
              <span className="dst-icon-users-x me-2" />
              {translate(language, "PAGES.MEETING.MEETING_TABS.ABSENCE_PROOFS")}
            </React.Fragment>
          ),
          content: <AbsenceProofs meetingId={meeting_id!} language={language} />,
        }
      : undefined,
    validateUserPermission([{ methods: ["GET"], route: "/meetings/*/logs" }], permissions)
      ? {
          id: "meeting_logs",
          title: (
            <React.Fragment>
              <span className="dst-icon-settings-04 me-2" />
              {translate(language, "PAGES.MEETING.MEETING_TABS.MEETING_LOGS")}
            </React.Fragment>
          ),
          content: <MeetingLogs meetingId={meeting_id!} language={language} />,
        }
      : undefined,
  ].filter((elem) => !!elem) as TabData[];

  useEffect(() => {
    if (meeting_id) {
      fetchMeeting(meeting_id, setMeeting, setLoaded);
    }
  }, [meeting_id]);

  useEffect(() => {
    const copyMeetingToClipboard = () => {
      if (!meeting?.name || !meeting?.type?.name || !meeting?.dt_start || !meeting?.dt_end) return;

      const meetingInfo = `[${meeting.name}](${window.location.href}) (${meeting.type.name}) [${_formatMeetingTime(
        meeting.dt_start,
        meeting.dt_end,
        language
      )}]`;

      navigator.clipboard
        .writeText(meetingInfo)
        .then(() => setShowCopyShortcutToast(true))
        .catch((error) => console.error("Failed to copy meeting info to clipboard:", error));
    };

    const listenDestructor = ShortcutService.listen("c", copyMeetingToClipboard, false, true);
    return () => listenDestructor();
  }, [language, meeting]);

  if (!loaded) {
    return (
      <div className="container-fluid mt-3 mb-5">
        <Placeholder animation="glow" className="d-flex align-items-center mb-3">
          <Placeholder as="h2" xs={1} className="me-2" />
          <Placeholder as="h2" xs={4} />
        </Placeholder>
        <DstTabs
          tabsData={tabsData}
          variant="primary"
          activeKey={activeKey}
          onSelect={(key) => {
            if (key) {
              setActiveKey(key);
            }
          }}
          mock
        />
      </div>
    );
  }
  if (!meeting) {
    return (
      <div className="h-100 d-flex justify-content-center align-items-center flex-column">
        <h2>{translate(language, "PAGES.MEETING.NOT_FOUND")}</h2>
        <DstButton
          btnClass="mt-3"
          value={translate(language, "PAGES.MEETING.ALL_MEETINGS")}
          clickFunction={() => navigate("/meetings")}
        />
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3 mb-5">
      {meeting && (
        <Helmet>
          <title>Meeting - {meeting.name}</title>
        </Helmet>
      )}
      <div className="d-flex align-items-center mb-3">
        <i
          className="dst-icon-chevron-left p-2 medium-gray-background rounded me-2 clickable"
          onClick={(event: any) => completeNavigate(event, navigate, "/meetings")}
        />
        <h1 className="mb-0 h3 me-3">
          {meeting.name}
          <span className="dark-gray-font mx-2">{_formatMeetingTime(meeting.dt_start, meeting.dt_end, language)}</span>
        </h1>
        <div className="d-flex align-items-center">
          {meeting &&
            getBadgeData(meeting, language).map((badge) => (
              <OverlayTrigger
                key={badge.key}
                placement="bottom"
                overlay={<Tooltip id={`tooltip-${badge.key}`}>{badge.description}</Tooltip>}
              >
                <div className="d-flex p-2 white-font rounded position-relative me-3 border">
                  <i className={`${badge.iconClass} blue-font`} style={{ fontSize: "1.2em" }} />
                  <span
                    className="position-absolute top-100 start-100 translate-middle badge rounded-pill blue-background"
                    style={{ fontSize: 10 }}
                  >
                    {badge.count}
                  </span>
                </div>
              </OverlayTrigger>
            ))}
        </div>
      </div>

      <DstTabs
        tabsData={tabsData}
        variant="primary"
        activeKey={activeKey}
        onSelect={(key) => {
          if (key) {
            setActiveKey(key);
          }
        }}
      />
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg="success"
          show={showCopyShortcutToast}
          onClose={() => setShowCopyShortcutToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{translate(language, "PAGES.MEETING.COPY_SHORTCUT_SUCCESS")}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Meeting;
